import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

export type EnvName = 'local' | 'production';

export interface EnvConfig {
  name: EnvName;
  label: string;
  apiUrl: string;
  color: string;
}

export const ENV_CONFIGS: Record<EnvName, EnvConfig> = {
  local: {
    name: 'local',
    label: 'LOCAL',
    apiUrl: 'http://localhost:5000/api',
    color: '#10B981',
  },
  production: {
    name: 'production',
    label: 'PRODUCTION',
    apiUrl: '/api',
    color: '#EF4444',
  },
};

const STORAGE_KEY = 'sm_admin_env';

@Injectable({ providedIn: 'root' })
export class ApiConfigService {
  /** Start from local if running on localhost, otherwise environment.ts */
  private readonly defaultEnv: EnvName = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'local'
    : ((environment.envName as EnvName) || 'local');

  private activeEnv: EnvConfig;
  private envSubject: BehaviorSubject<EnvConfig>;

  env$: ReturnType<typeof this.envSubject.asObservable>;

  constructor() {
    const saved = localStorage.getItem(STORAGE_KEY) as EnvName | null;
    const initial: EnvName = (saved && ENV_CONFIGS[saved]) ? saved : this.defaultEnv;
    this.activeEnv = ENV_CONFIGS[initial];
    this.envSubject = new BehaviorSubject<EnvConfig>(this.activeEnv);
    this.env$ = this.envSubject.asObservable();
  }

  get currentEnv(): EnvConfig {
    return this.envSubject.value;
  }

  get apiUrl(): string {
    return this.envSubject.value.apiUrl;
  }

  switchEnv(name: EnvName): void {
    const config = ENV_CONFIGS[name];
    if (!config) return;
    localStorage.setItem(STORAGE_KEY, name);
    this.envSubject.next(config);
  }

  isLocal(): boolean {
    return this.currentEnv.name === 'local';
  }

  isProduction(): boolean {
    return this.currentEnv.name === 'production';
  }
}
